<script>
/**
 * @component WhatsappAuditPanel
 * @description Panel de auditoría unificado de toda la actividad WhatsApp.
 *              Muestra el log canónico `WhatsappInteraction` — cada fila
 *              representa un par outbound↔inbound (lo que el bot envió +
 *              lo que el paciente respondió).
 *
 *              Filtros: paciente | tipo de tarea | dirección | estado |
 *                       confidence máxima | rango fechas
 *              Columnas: hora, paciente, tipo, tarea, bot envió, paciente
 *                        dijo, LLM (intent+conf), estado
 *              Click en fila → modal con detalle completo (outbound, inbound,
 *                              system prompt usado, raw response del LLM,
 *                              task, conversation, action aplicada)
 *
 * @example <WhatsappAuditPanel />
 */
import whatsappConfigService from "@/services/whatsappConfig";
import DispatchTaskModal from "./DispatchTaskModal.vue";

const PAGE_SIZE = 20;

export default {
  name: "WhatsappAuditPanel",

  components: { DispatchTaskModal },

  data() {
    return {
      loading: false,
      interactions: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,

      filters: {
        confidenceMax: null,
        taskType: null, // 'vital' | 'checkin' | 'medication' | null (todos)
        status: null, // awaiting_response | completed | expired | flagged_review | standalone
        direction: null, // 'inbound' | 'outbound' | null (todos = both)
        from: null,
        to: null,
      },

      detail: {
        visible: false,
        loading: false,
        item: null,
      },

      // Modal de dispatch manual de tarea (botón "+ Disparar tarea")
      dispatchModalVisible: false,
    };
  },

  async mounted() {
    await this.load();
  },

  methods: {
    /**
     * @description Carga interacciones desde el backend con los filtros activos.
     */
    async load() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.pageSize,
        };
        if (
          this.filters.confidenceMax != null &&
          this.filters.confidenceMax > 0
        ) {
          params.confidenceMax = this.filters.confidenceMax;
        }
        if (this.filters.taskType) params.taskType = this.filters.taskType;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.direction) params.direction = this.filters.direction;
        if (this.filters.from) params.from = this.filters.from;
        if (this.filters.to) params.to = this.filters.to;

        const { data } = await whatsappConfigService.listInteractions(params);
        this.interactions = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        this.$message.error("No se pudieron cargar las interacciones");
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Resetea filtros y recarga.
     */
    resetFilters() {
      this.filters = {
        confidenceMax: null,
        taskType: null,
        status: null,
        direction: null,
        from: null,
        to: null,
      };
      this.page = 1;
      this.load();
    },

    /**
     * @description Etiqueta del tipo de tarea en español.
     * @param {string} type - 'vital' | 'checkin' | 'medication' | 'health-conversation'
     */
    taskTypeLabel(type) {
      const map = {
        vital: "Vital",
        checkin: "Check-in",
        medication: "Medicación",
        "health-conversation": "Conversación de salud",
      };
      return map[type] || type || "—";
    },

    /**
     * @description Color del tag según tipo de tarea.
     */
    taskTypeTagType(type) {
      const map = {
        vital: "danger",
        checkin: "info",
        medication: "warning",
        "health-conversation": "success",
      };
      return map[type] || "";
    },

    /**
     * @description Color del tag según status de la interaction.
     */
    statusTagType(status) {
      const map = {
        awaiting_response: "warning",
        completed: "success",
        expired: "info",
        flagged_review: "danger",
        standalone: "info",
        abandoned: "info",
      };
      return map[status] || "";
    },

    /**
     * @description Etiqueta del status.
     */
    statusLabel(status) {
      const map = {
        awaiting_response: "Esperando respuesta",
        completed: "Completada",
        expired: "Expirada",
        flagged_review: "⚠ Revisión",
        standalone: "Solo entrante",
        abandoned: "Descartada",
      };
      return map[status] || status || "—";
    },

    /**
     * @description UC-REG-151 — Descarta N interactions viejas para
     *              descargar la vista. Pide antigüedad en días al usuario
     *              (default 7). El endpoint marca todos los awaiting_response
     *              con `createdAt < now - N días` como `abandoned`. Historia
     *              intacta — filtro "abandoned" para volver a verlas.
     */
    async bulkAbandonOld() {
      let daysStr;
      try {
        const p = await this.$prompt(
          "Antigüedad mínima en días (todas las 'Esperando respuesta' más viejas se marcarán como Descartadas):",
          "Descartar interacciones viejas",
          {
            confirmButtonText: "Descartar",
            cancelButtonText: "Cancelar",
            inputValue: "7",
            inputPattern: /^[1-9]\d*$/,
            inputErrorMessage: "Ingresá un entero >= 1",
          }
        );
        daysStr = p.value;
      } catch {
        return; // usuario canceló
      }
      const days = Number(daysStr);
      try {
        const res = await whatsappConfigService.bulkAbandonInteractions({
          olderThanDays: days,
        });
        this.$message.success(
          `${res.modifiedCount} interacciones descartadas (> ${days} días)`
        );
        await this.load();
      } catch (e) {
        this.$message.error(
          e.response?.data?.message || "No se pudieron descartar"
        );
      }
    },

    onPageChange(page) {
      this.page = page;
      this.load();
    },

    onFiltersChange() {
      this.page = 1;
      this.load();
    },

    confidenceClass(c) {
      if (c == null) return "audit-panel__conf--none";
      if (c >= 0.85) return "audit-panel__conf--green";
      if (c >= 0.6) return "audit-panel__conf--yellow";
      return "audit-panel__conf--red";
    },

    formatValue(v) {
      if (v === null || v === undefined) return "—";
      if (typeof v === "boolean") return v ? "Sí" : "No";
      return String(v);
    },

    intentTagType(intent) {
      switch (intent) {
        case "completed":
        case "matched":
          return "success";
        case "skipped":
          return "info";
        case "unclear":
        case "unrelated":
          return "warning";
        case "error":
          return "danger";
        default:
          return "info";
      }
    },

    /**
     * @description Trunca un texto largo para mostrar en tabla.
     */
    truncate(t, n = 60) {
      if (!t) return "—";
      const s = String(t);
      return s.length > n ? s.substring(0, n) + "…" : s;
    },

    /**
     * @description Cantidad total de intentos en esta fila.
     *              `previousAttempts` archiva los intentos previos; el actual
     *              vive en `outbound`/`inbound` del row → +1.
     * @param {Object} row
     * @returns {number}
     */
    attemptsCount(row) {
      const prev = Array.isArray(row.previousAttempts)
        ? row.previousAttempts.length
        : 0;
      return prev + 1;
    },

    /**
     * @description Formato compacto de fecha — 24hs sin am/pm + TZ forzada a
     *              Argentina. `toLocaleString` por defecto usa la TZ del
     *              browser, lo que produce desfases si el cliente está en
     *              otra región. Forzamos `America/Argentina/Buenos_Aires`
     *              porque la app es para uso clínico en Argentina.
     *              `toLocaleString('es-AR')` inserta una coma entre fecha y
     *              hora ("14/5, 20:28") que rompe el layout de la columna
     *              estrecha del panel; la reemplazamos por espacio para que
     *              la celda entre en una sola línea. Ej: "14/5 17:13".
     */
    formatDate(d) {
      if (!d) return "—";
      const date = new Date(d);
      return date
        .toLocaleString("es-AR", {
          timeZone: "America/Argentina/Buenos_Aires",
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "");
    },

    async openDetail(row) {
      this.detail.visible = true;
      this.detail.loading = true;
      this.detail.item = null;
      try {
        const { data } = await whatsappConfigService.getInteraction(row._id);
        this.detail.item = data;
      } catch (e) {
        this.$message.error("No se pudo cargar el detalle");
      } finally {
        this.detail.loading = false;
      }
    },

    /**
     * @description Abre el modal de dispatch manual de tarea.
     */
    openDispatchModal() {
      this.dispatchModalVisible = true;
    },

    /**
     * @description Callback del modal cuando se disparó OK una tarea.
     *              Refresca la tabla para que aparezca la nueva
     *              Interaction `awaiting_response`.
     */
    onTaskDispatched() {
      this.load();
    },
  },
};
</script>

<template lang="pug">
  .audit-panel(v-loading="loading")
    //- El título "Auditoría WhatsApp" lo pinta SettingsDashboard.
    //- Header del card: 2 acciones principales arriba de la tabla.
    //- (Antes había un texto descriptivo "Log unificado..." pero era ruido,
    //- el panel se entiende sin él.)
    .audit-panel__actions
      el-button(size="small" type="primary" icon="el-icon-refresh" :loading="loading" @click="load") Actualizar
      el-button(size="small" type="success" icon="el-icon-message" @click="openDispatchModal") + Disparar tarea
      //- UC-REG-151 — botón para descartar en bulk interactions viejas
      el-button(size="small" icon="el-icon-delete" @click="bulkAbandonOld") Descartar viejas

    .audit-panel__filters
      .audit-panel__filter
        label.audit-panel__filter-label Confidence máx.
        el-input(
          v-model.number="filters.confidenceMax"
          type="number"
          step="0.05"
          min="0"
          max="1"
          placeholder="vacío = todos"
          @change="onFiltersChange"
          clearable
        )
      .audit-panel__filter
        label.audit-panel__filter-label Desde
        el-date-picker(
          v-model="filters.from"
          type="date"
          placeholder="—"
          value-format="yyyy-MM-dd"
          @change="onFiltersChange"
        )
      .audit-panel__filter
        label.audit-panel__filter-label Hasta
        el-date-picker(
          v-model="filters.to"
          type="date"
          placeholder="—"
          value-format="yyyy-MM-dd"
          @change="onFiltersChange"
        )
      .audit-panel__filter
        label.audit-panel__filter-label Tipo
        el-select(
          v-model="filters.taskType"
          placeholder="Todos"
          clearable
          @change="onFiltersChange"
          style="width: 140px"
        )
          el-option(label="Vital" value="vital")
          el-option(label="Check-in" value="checkin")
          el-option(label="Medicación" value="medication")
          el-option(label="Conversación de salud" value="health-conversation")
      .audit-panel__filter
        label.audit-panel__filter-label Dirección
        el-select(
          v-model="filters.direction"
          placeholder="Ambas"
          clearable
          @change="onFiltersChange"
          style="width: 140px"
        )
          el-option(label="Solo bot envió" value="outbound")
          el-option(label="Solo paciente" value="inbound")
      .audit-panel__filter
        label.audit-panel__filter-label Estado
        el-select(
          v-model="filters.status"
          placeholder="Todos"
          clearable
          @change="onFiltersChange"
          style="width: 180px"
        )
          el-option(label="Esperando respuesta" value="awaiting_response")
          el-option(label="Completada" value="completed")
          el-option(label="Expirada" value="expired")
          el-option(label="Revisión humana" value="flagged_review")
          el-option(label="Solo entrante" value="standalone")
      .audit-panel__filter
        el-button(size="small" @click="resetFilters") Limpiar

    el-table(
      :data="interactions"
      stripe
      @row-click="openDetail"
      style="cursor: pointer"
    )
      el-table-column(label="Hora" width="120")
        template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}

      el-table-column(label="Paciente" width="140")
        template(slot-scope="{ row }")
          template(v-if="row.patient")
            | {{ row.patient.firstName }} {{ row.patient.lastName }}
          template(v-else) —

      el-table-column(label="Tipo" width="140")
        template(slot-scope="{ row }")
          .audit-panel__type-cell
            el-tag(:type="taskTypeTagType(row.taskType)" size="small")
              | {{ taskTypeLabel(row.taskType) }}
            span.audit-panel__qbadge(v-if="row.questionIdx != null")
              | · Q{{ row.questionIdx + 1 }}

      el-table-column(label="Tarea" width="160" show-overflow-tooltip)
        template(slot-scope="{ row }")
          | {{ (row.taskRef && row.taskRef.name) || '—' }}

      el-table-column(label="Bot envió" min-width="220" show-overflow-tooltip)
        template(slot-scope="{ row }")
          .audit-panel__bubble.audit-panel__bubble--out(v-if="row.outbound && row.outbound.text")
            | {{ truncate(row.outbound.text, 110) }}
          template(v-else) —

      el-table-column(label="Paciente dijo" min-width="180" show-overflow-tooltip)
        template(slot-scope="{ row }")
          .audit-panel__bubble.audit-panel__bubble--in(v-if="row.inbound && row.inbound.text")
            | {{ truncate(row.inbound.text, 90) }}
          template(v-else-if="row.inbound && row.inbound.attachmentUrl") 📷 (imagen)
          template(v-else-if="row.status === 'awaiting_response'") ⏳ esperando
          template(v-else) —
          //- Badge con cantidad de intentos previos (= reformulaciones que
          //- el LLM no pudo resolver antes de cerrar la pregunta).
          .audit-panel__attempts-badge(v-if="attemptsCount(row) > 1")
            | {{ attemptsCount(row) }} intentos

      el-table-column(label="LLM" width="140")
        template(slot-scope="{ row }")
          template(v-if="row.inbound && row.inbound.llm && row.inbound.llm.intent")
            .audit-panel__llm-cell
              el-tag(:type="intentTagType(row.inbound.llm.intent)" size="mini")
                | {{ row.inbound.llm.intent }}
              span.audit-panel__conf(:class="confidenceClass(row.inbound.llm.confidence)" v-if="row.inbound.llm.confidence != null")
                | {{ (row.inbound.llm.confidence * 100).toFixed(0) }}%
          template(v-else) —

      el-table-column(label="Estado" width="160")
        template(slot-scope="{ row }")
          el-tag(:type="statusTagType(row.status)" size="small")
            | {{ statusLabel(row.status) }}

    .audit-panel__pagination(v-if="total > pageSize")
      el-pagination(
        layout="prev, pager, next, total"
        :total="total"
        :current-page="page"
        :page-size="pageSize"
        @current-change="onPageChange"
      )

    //- ──────── DIALOG DETALLE ────────
    el-dialog(
      title="Detalle de la interacción"
      :visible.sync="detail.visible"
      width="780px"
      v-loading="detail.loading"
    )
      .audit-detail(v-if="detail.item")
        //- Resumen
        .audit-detail__row
          .audit-detail__col
            .audit-detail__label Paciente
            .audit-detail__value
              template(v-if="detail.item.patient")
                | {{ detail.item.patient.firstName }} {{ detail.item.patient.lastName }}
                br
                small.audit-detail__phone {{ detail.item.patient.phone }}
              template(v-else) —
          .audit-detail__col
            .audit-detail__label Tipo / Tarea
            .audit-detail__value
              el-tag(:type="taskTypeTagType(detail.item.taskType)" size="small")
                | {{ taskTypeLabel(detail.item.taskType) }}
              br
              | {{ (detail.item.taskRef && detail.item.taskRef.name) || '—' }}
              template(v-if="detail.item.questionIdx != null")
                |  · Pregunta {{ detail.item.questionIdx + 1 }}
          .audit-detail__col
            .audit-detail__label Estado
            .audit-detail__value
              el-tag(:type="statusTagType(detail.item.status)" size="small")
                | {{ statusLabel(detail.item.status) }}

        //- Historial de intentos previos (si los hay).
        //- Cada intento anterior tuvo su outbound + inbound, pero el LLM no
        //- pudo resolver con suficiente confidence y el bot reformuló.
        .audit-detail__section(v-if="detail.item.previousAttempts && detail.item.previousAttempts.length")
          .audit-detail__section-title
            | Historial de intentos ({{ detail.item.previousAttempts.length }} previos antes de cerrar)
          .audit-detail__attempt(v-for="(att, i) in detail.item.previousAttempts" :key="'att-' + i")
            .audit-detail__attempt-header Intento {{ i + 1 }}
            .audit-detail__bubble.audit-detail__bubble--out(v-if="att.outbound && att.outbound.text")
              | {{ att.outbound.text }}
              .audit-detail__time {{ formatDate(att.outbound.sentAt) }}
            .audit-detail__bubble.audit-detail__bubble--in(v-if="att.inbound && att.inbound.text")
              | {{ att.inbound.text }}
              .audit-detail__time {{ formatDate(att.inbound.receivedAt) }}
            .audit-detail__attempt-llm(v-if="att.inbound && att.inbound.llm && att.inbound.llm.intent")
              el-tag(:type="intentTagType(att.inbound.llm.intent)" size="mini")
                | {{ att.inbound.llm.intent }}
              span.audit-panel__conf(:class="confidenceClass(att.inbound.llm.confidence)" v-if="att.inbound.llm.confidence != null")
                | {{ (att.inbound.llm.confidence * 100).toFixed(0) }}%
              span.audit-detail__attempt-action(v-if="att.inbound.appliedAction")
                | → {{ att.inbound.appliedAction }}

        //- Outbound actual (el último — el que cerró el row o sigue abierto)
        .audit-detail__section(v-if="detail.item.outbound")
          .audit-detail__section-title
            template(v-if="detail.item.previousAttempts && detail.item.previousAttempts.length")
              | Intento final · Bot envió ({{ detail.item.outbound.triggeredBy }})
            template(v-else)
              | Bot envió ({{ detail.item.outbound.triggeredBy }})
          .audit-detail__bubble.audit-detail__bubble--out
            | {{ detail.item.outbound.text }}
            .audit-detail__time
              | {{ formatDate(detail.item.outbound.sentAt) }}
              span(v-if="detail.item.outbound.status === 'failed'") · ❌ {{ detail.item.outbound.error }}

        //- Inbound
        .audit-detail__section(v-if="detail.item.inbound && detail.item.inbound.text")
          .audit-detail__section-title Paciente dijo (vía {{ detail.item.inbound.via }})
          .audit-detail__bubble.audit-detail__bubble--in
            | {{ detail.item.inbound.text }}
            img.audit-detail__attachment(
              v-if="detail.item.inbound.attachmentUrl"
              :src="detail.item.inbound.attachmentUrl"
              alt="adjunto"
            )
            .audit-detail__time {{ formatDate(detail.item.inbound.receivedAt) }}

        //- LLM
        .audit-detail__section(v-if="detail.item.inbound && detail.item.inbound.llm && detail.item.inbound.llm.intent")
          .audit-detail__section-title Decisión del LLM
          .audit-detail__row
            .audit-detail__col
              .audit-detail__label Provider / Modelo
              .audit-detail__value
                | {{ detail.item.inbound.llm.provider }} / {{ detail.item.inbound.llm.model }}
            .audit-detail__col
              .audit-detail__label Latencia / Tokens
              .audit-detail__value
                | {{ detail.item.inbound.llm.latencyMs }}ms · {{ detail.item.inbound.llm.tokensIn || 0 }}+{{ detail.item.inbound.llm.tokensOut || 0 }}
            .audit-detail__col
              .audit-detail__label Confidence
              .audit-detail__value
                span.audit-panel__conf(:class="confidenceClass(detail.item.inbound.llm.confidence)")
                  | {{ detail.item.inbound.llm.confidence != null ? (detail.item.inbound.llm.confidence * 100).toFixed(0) + '%' : '—' }}
          .audit-detail__row
            .audit-detail__col
              .audit-detail__label Intent
              .audit-detail__value
                el-tag(:type="intentTagType(detail.item.inbound.llm.intent)") {{ detail.item.inbound.llm.intent }}
            .audit-detail__col
              .audit-detail__label Acción aplicada
              .audit-detail__value
                | {{ detail.item.inbound.appliedAction || '—' }}
            .audit-detail__col
              .audit-detail__label Valor parseado
              .audit-detail__value
                | {{ formatValue(detail.item.inbound.llm.parsedValue) || formatValue(detail.item.inbound.llm.selectedOptionId) }}

          el-collapse(v-if="detail.item.inbound.llm.rawResponse")
            el-collapse-item(title="Ver raw response del LLM")
              pre.audit-detail__pre {{ JSON.stringify(detail.item.inbound.llm.rawResponse, null, 2) }}

    //- Modal de dispatch manual de tarea
    DispatchTaskModal(
      :visible.sync="dispatchModalVisible"
      @dispatched="onTaskDispatched"
    )
</template>

<style lang="scss" scoped>
.audit-panel {
  // Reducir font de toda la tabla — más densidad, menos scroll
  ::v-deep .el-table {
    font-size: 12px;

    th,
    td {
      padding: 6px 8px;
    }
    .cell {
      line-height: 1.4;
    }
  }
  ::v-deep .el-tag--mini {
    height: 18px;
    padding: 0 5px;
    line-height: 16px;
    font-size: 11px;
  }

  // Reemplaza al `__caption` viejo: ahora encima de los filtros van los
  // 2 botones de acción principales (Actualizar + Disparar tarea), alineados
  // a la izquierda, sin texto descriptivo.
  &__actions {
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 14px;
    background: #f9fafb;
    border-radius: 8px;
    margin-bottom: 14px;
    font-size: 12px;
  }

  &__filter {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__filter-label {
    font-size: 11px;
    font-weight: 500;
    color: #374151;
  }

  // Tag + % en una sola línea, alineados con gap
  &__llm-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  &__type-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  &__qbadge {
    font-size: 11px;
    color: #6b7280;
  }

  // Badge "(N intentos)" debajo de la burbuja del paciente cuando hubo
  // reintentos de aclaración archivados en previousAttempts[].
  &__attempts-badge {
    display: inline-block;
    margin-top: 4px;
    padding: 1px 6px;
    border-radius: 8px;
    background: #fef3c7;
    color: #92400e;
    font-size: 10px;
    font-weight: 600;
  }

  &__bubble {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 11px;
    line-height: 1.3;
    max-width: 100%;
    &--out {
      background: #eef2ff;
      color: #312e81;
    }
    &--in {
      background: #d1fae5;
      color: #064e3b;
    }
  }

  &__conf {
    display: inline-block;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 10px;
    line-height: 1.2;
    &--green {
      background: #d1fae5;
      color: #065f46;
    }
    &--yellow {
      background: #fef3c7;
      color: #92400e;
    }
    &--red {
      background: #fee2e2;
      color: #991b1b;
    }
    &--none {
      background: #e5e7eb;
      color: #6b7280;
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }
}

.audit-detail {
  &__row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  &__col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-top: 8px;
  }

  &__value {
    font-size: 14px;
    color: #111827;
  }

  &__phone {
    color: #6b7280;
    font-size: 11px;
  }

  &__section {
    border-top: 1px solid #e5e7eb;
    padding-top: 12px;
    margin-top: 12px;
  }

  &__section-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 8px;
  }

  &__bubble {
    padding: 10px 14px;
    border-radius: 12px;
    max-width: 100%;
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 4px;
    white-space: pre-wrap;
    &--out {
      background: #eef2ff;
      color: #312e81;
    }
    &--in {
      background: #d1fae5;
      color: #064e3b;
    }
  }

  &__time {
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
  }

  &__attachment {
    display: block;
    margin-top: 8px;
    max-width: 200px;
    border-radius: 8px;
  }

  &__pre {
    background: #1f2937;
    color: #d1d5db;
    padding: 12px;
    border-radius: 6px;
    font-size: 11px;
    max-height: 240px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }

  // Bloque de intento previo (dentro del modal de detalle).
  &__attempt {
    background: #f9fafb;
    border-left: 3px solid #d1d5db;
    padding: 8px 12px;
    margin-bottom: 8px;
    border-radius: 4px;

    .audit-detail__bubble {
      font-size: 12px;
      padding: 6px 10px;
      margin-bottom: 4px;
    }
  }

  &__attempt-header {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  &__attempt-llm {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 11px;
  }

  &__attempt-action {
    color: #6b7280;
    font-style: italic;
  }
}
</style>
